import {
  Alert,
  Box,
  Button,
  Container,
  Link,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthService from "../../api/authService";
import { useForm, useWatch } from "react-hook-form";

const RegisterForm = () => {
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
  } = useForm();

  // null = still checking, true = open (no users yet), false = closed
  const [registrationOpen, setRegistrationOpen] = useState(null);

  const navigate = useNavigate();
  const password = useWatch({ control, name: "password", defaultValue: "" });

  useEffect(() => {
    let cancelled = false;
    AuthService.isRegistrationOpen()
      .then((open) => {
        if (!cancelled) setRegistrationOpen(open);
      })
      .catch(() => {
        // If we cannot reach the backend, let the submit surface the error.
        if (!cancelled) setRegistrationOpen(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const onSubmit = async (data) => {
    // Reset error
    setError("");

    try {
      setLoading(true);
      // Call register method from AuthService
      await AuthService.register({
        username: data.username,
        password: data.password,
      });

      // Redirect to login page after successful registration
      navigate("/admin/login");
    } catch (err) {
      setError(
        err.response?.data?.message || "Registration failed. Please try again.",
      );
      console.error("Registration error:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Paper
        elevation={3}
        sx={{
          marginTop: 8,
          padding: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Typography component="h1" variant="h5">
          Create Admin Account
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
          This page only works until the first admin user has been created
        </Typography>

        {registrationOpen === false && (
          <>
            <Alert severity="warning" sx={{ mt: 2, width: "100%" }}>
              Registration is closed because an admin account already exists.
              Ask an existing admin to create your account from the Users page.
            </Alert>
            <Box sx={{ textAlign: "center", mt: 3 }}>
              <Link href="/admin/login" variant="body2">
                Go to login
              </Link>
            </Box>
          </>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2, width: "100%" }}>
            {error}
          </Alert>
        )}

        {registrationOpen && (
          <Box
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            sx={{ mt: 3 }}
          >
            <TextField
              margin="normal"
              required
              fullWidth
              id="username"
              label="Username"
              autoComplete="username"
              autoFocus
              error={!!errors.username}
              helperText={errors.username?.message}
              {...register("username", {
                required: "Username is required",
              })}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label="Password"
              type="password"
              id="password"
              autoComplete="new-password"
              error={!!errors.password}
              helperText={errors.password?.message}
              {...register("password", {
                required: "Password is required",
                minLength: {
                  value: 6,
                  message: "Password must be at least 6 characters long",
                },
              })}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label="Confirm Password"
              type="password"
              id="confirmPassword"
              autoComplete="new-password"
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword?.message}
              {...register("confirmPassword", {
                required: "Please confirm your password",
                validate: (value) =>
                  value === password || "Passwords do not match",
              })}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? "Creating Account..." : "Register"}
            </Button>
            <Box sx={{ textAlign: "center", mt: 2 }}>
              <Link href="/admin/login" variant="body2">
                Already have an account? Login
              </Link>
            </Box>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default RegisterForm;
